import { canonicalJson, bumpArcadeConfigV1, territoryConfigV1, shooterMissionConfigV1, matchPuzzleConfigV1, serviceGameConfigV1, courierBoostConfigV1,
  type BumpArcadeConfigV1, type TerritoryConfigV1, type ShooterMissionConfigV1, type MatchPuzzleConfigV1, type ServiceGameConfigV1, type CourierBoostConfigV1 } from '@djgg/gameplay-kit';
import type { GameQualityRequirementV2 } from '../qualitySchemas.js';

export const MAINTAINED_PROFILE_IDS = ['bump-arcade@1', 'territory@1', 'shooter-mission@1', 'match-puzzle@1', 'service-game@1', 'courier-boost@1'] as const;
export type MaintainedProfileIdV1 = typeof MAINTAINED_PROFILE_IDS[number];
export type MaintainedVariantV1 = 'standard' | 'alternate';
export interface MaintainedGameSpecV1 {
  schemaVersion: 1; profile: MaintainedProfileIdV1; variant: MaintainedVariantV1;
  title: string; theme: 'arcade' | 'night';
}
export type MaintainedConfigV1 = BumpArcadeConfigV1 | TerritoryConfigV1 | ShooterMissionConfigV1 | MatchPuzzleConfigV1 | ServiceGameConfigV1 | CourierBoostConfigV1;
export function validateMaintainedGameSpecV1(input: unknown): MaintainedGameSpecV1 {
  if (!input || typeof input !== 'object' || Array.isArray(input)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(input))
    || Reflect.ownKeys(input).length !== 5
    || Object.keys(input).sort().join(',') !== 'profile,schemaVersion,theme,title,variant'
    || Object.values(Object.getOwnPropertyDescriptors(input)).some(d => !('value' in d))) throw new Error('Invalid maintained composition specification');
  const value = input as Record<string, unknown>;
  if (value.schemaVersion !== 1 || !MAINTAINED_PROFILE_IDS.includes(value.profile as MaintainedProfileIdV1)
    || !['standard', 'alternate'].includes(String(value.variant)) || !['arcade', 'night'].includes(String(value.theme))
    || value.profile === 'courier-boost@1' && value.variant !== 'standard'
    || typeof value.title !== 'string' || value.title.length < 1 || value.title.length > 80
    || /[\u0000-\u001f<>]/.test(value.title) || value.title.trim() !== value.title) throw new Error('Unsupported maintained composition specification');
  return structuredClone(value) as unknown as MaintainedGameSpecV1;
}
export function maintainedConfigV1(spec: MaintainedGameSpecV1): MaintainedConfigV1 {
  switch (spec.profile) {
    case 'bump-arcade@1': return bumpArcadeConfigV1(spec.variant);
    case 'territory@1': return territoryConfigV1(spec.variant);
    case 'shooter-mission@1': return shooterMissionConfigV1(spec.variant);
    case 'match-puzzle@1': return matchPuzzleConfigV1(spec.variant);
    case 'service-game@1': return serviceGameConfigV1(spec.variant);
    case 'courier-boost@1': return courierBoostConfigV1();
  }
}
/** Admission is an exact maintained-data lookup, never an arbitrary tuning or
 * executable extension surface. Changed mechanics require a new profile. */
export function validateMaintainedConfigV1(spec: MaintainedGameSpecV1, input: unknown): MaintainedConfigV1 {
  const expected = maintainedConfigV1(spec);
  if (canonicalJson(input) !== canonicalJson(expected)) throw new Error('Unsupported maintained mechanics configuration');
  return expected;
}
export function maintainedRequirementsV1(spec: MaintainedGameSpecV1): GameQualityRequirementV2[] {
  type Rule = [string, GameQualityRequirementV2['semantic']['kind'], string[], string[]];
  const rules: Rule[] = spec.profile === 'bump-arcade@1' ? [
    ['bump-stun-kick', 'motion', ['bump', 'kick'], ['underside-contact', 'stun-before-kick', 'single-removal-score']],
    ['pow-waves', 'progression', ['pow', 'clear'], ['bounded-pow', 'pipe-return', 'all-waves-clear']],
  ] : spec.profile === 'territory@1' ? [
    ['route-capture', 'progression', ['move', 'capture'], ['closed-public-trail', 'enclosed-region-fill', 'actor-attribution']],
    ['continuous-hazards', 'motion', ['avoid'], ['continuous-motion', 'trail-cut', 'capture-target-and-safe-return']],
  ] : spec.profile === 'match-puzzle@1' ? [
    ['swap-match', 'progression', ['swap'], ['adjacent-public-swap', 'invalid-swap-restored', 'finite-moves']],
    ['cascade-refill', 'progression', ['match'], ['deterministic-refill', 'bounded-cascades', 'complete-clear-target']],
  ] : spec.profile === 'service-game@1' ? [
    ['recipe-service', 'progression', ['pick', 'prep', 'cook', 'serve'], ['ordered-recipe', 'finite-stock', 'serve-once']],
    ['complete-orders', 'progression', ['serve'], ['all-orders', 'score-300', 'no-duplicate-score']],
  ] : spec.profile === 'courier-boost@1' ? [
    ['run-jump', 'motion', ['run', 'jump'], ['fixed-step-solid-contact']],
    ['collect-all-exit', 'progression', ['collect', 'exit'], ['three-distinct-parcels', 'score-800', 'winner-seat-0']],
    ['bounded-boost', 'motion', ['collect'], ['next-tick-speed-only', 'nonstacking-1.1', 'four-active-ticks', 'reset-expiry']],
  ] : [
    ['cannon-bomb', 'motion', ['move', 'cannon', 'bomb'], ['swept-projectile-contact', 'victim-isolation', 'bounded-bomb']],
    ['complete-mission', 'progression', ['clear'], ['all-waves-clear', 'mission-distance', 'no-score-only-win']],
  ];
  rules.push(['loss-restart', 'lifecycle', ['lose', 'restart'], [spec.profile === 'match-puzzle@1' ? 'moves-loss' : spec.profile === 'service-game@1' ? 'mistakes-and-resources-loss' : 'lives-loss', 'time-loss', 'fresh-state']],
    ['native-controls', 'agency', ['move', 'action'], ['desktop', 'touch', 'full-win-loss-restart']]);
  return rules.map(([id, kind, actions, consequences]) => ({
    id, mandatory: true, provenance: { source: 'approved-design', reference: `${spec.profile}/${spec.variant}` },
    semantic: { kind, revision: 1, parameters: {} }, actors: ['player'], targets: [], preconditions: [], actions, consequences,
    forbiddenOutcomes: ['requirement-omitted'], obligations: [
      { stage: 'final', evidenceKind: 'deterministic-simulation', device: 'deterministic' },
      ...(id === 'native-controls' ? [
        { stage: 'final' as const, evidenceKind: 'native-browser' as const, device: 'desktop' as const },
        { stage: 'final' as const, evidenceKind: 'native-browser' as const, device: 'touch' as const },
      ] : []),
    ],
  }));
}
